export const FIXED_FIELDS = [
  { key: "logo", label: "Logo" },
  { key: "productImage", label: "Product Image" },
  { key: "productTitle", label: "Product Title" },
  { key: "size", label: "Size" },
  { key: "material", label: "Material" },
  { key: "pieces", label: "Pieces" },
  { key: "description", label: "Description" },
  { key: "upc", label: "UPC" },
];

export const TEXT_FIELDS = ["productTitle", "size", "material", "pieces", "description"];

export const BG_CATEGORY_MAP = {
  Bedding: "bedding",
  Curtain: "curtain",
  "Table Linen": "tableLinen",
  Placemat: "placemat",
  Throw: "throw",
};

export function defaultAddedText() {
  return { id: crypto.randomUUID(), x: 20, y: 20, width: 90, height: 20, text: "New Text", fontSize: 8, bold: false, align: "left", color: "#1E293B" };
}

export function defaultAddedImage() {
  return { id: crypto.randomUUID(), x: 20, y: 20, width: 60, height: 60, url: "" };
}

export const QUICK_SWATCHES = [
  { label: "白", hex: "#FFFFFF" },
  { label: "黑", hex: "#000000" },
  { label: "米白", hex: "#F5F0E1" },
  { label: "藏青", hex: "#1B2A4A" },
  { label: "卡其", hex: "#C3B091" },
];

export const FONT_STACK_MAP = {
  "sans-serif": "Arial, Helvetica, sans-serif",
  "serif": "Georgia, 'Times New Roman', serif",
  "monospace": "'Courier New', monospace",
  "condensed-sans-serif": "'Arial Narrow', 'Helvetica Neue Condensed', sans-serif",
};

export const FONT_OPTIONS = [
  { value: "sans-serif", label: "无衬线 Sans-serif" },
  { value: "serif", label: "衬线 Serif" },
  { value: "monospace", label: "等宽 Monospace" },
  { value: "condensed-sans-serif", label: "紧凑无衬线 Condensed" },
];

export function resolveFontStack(key) {
  const k = (key || "").trim();
  if (!k) return FONT_STACK_MAP["sans-serif"];
  return FONT_STACK_MAP[k.toLowerCase()] || k;
}

export function classifyFromStack(font) {
  if (!font) return "sans-serif";
  const f = String(font).trim();
  const fl = f.toLowerCase();
  if (FONT_STACK_MAP[fl]) return fl;
  for (const [k, v] of Object.entries(FONT_STACK_MAP)) {
    if (v.toLowerCase() === fl) return k;
  }
  if (fl.includes("narrow") || fl.includes("condensed")) return "condensed-sans-serif";
  if (fl.includes("mono") || fl.includes("courier")) return "monospace";
  if (!f.includes(",")) return f;
  if (fl.includes("serif") && !fl.includes("sans")) return "serif";
  return "sans-serif";
}

export const CATEGORY_LABELS = {
  productPhoto: "产品图片",
  logo: "Logo",
  careInstruction: "洗涤说明",
  material: "材质",
  description: "产品描述",
  sizeChart: "尺寸表",
  barcode: "条码",
  other: "其他",
};

export const CATEGORY_ORDER = ["productPhoto", "logo", "careInstruction", "material", "description", "sizeChart", "barcode", "other"];

export function defaultOverlay(type) {
  const base = { id: crypto.randomUUID(), type, x: 40, y: 40, width: type === "text" ? 300 : 200, height: type === "text" ? 60 : 120 };
  if (type === "text") return { ...base, text: "New Text", font: "sans-serif", fontSize: 32, bold: false, align: "left", color: "#111111", category: "other" };
  if (type === "upc") return { ...base, text: "012345678901", width: 220, height: 90, category: "barcode" };
  if (type === "cover") return { ...base, color: "#ffffff", category: "other" };
  if (type === "logo") return { ...base, url: "", category: "logo" };
  return { ...base, url: "", category: "other" };
}

export async function uploadAndGetUrl(api, file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
}

export async function uploadLogoAndGetUrl(api, file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return { url: data.url, width: data.width, height: data.height };
}

export function fitLogoBox(box, imgW, imgH) {
  if (!box || !imgW || !imgH) return null;
  const aspect = imgW / imgH;
  const area = box.width * box.height;
  if (area <= 0 || aspect <= 0) return null;
  const newW = Math.sqrt(area * aspect);
  const newH = Math.sqrt(area / aspect);
  const cx = box.x + box.width / 2;
  const cy = box.y + box.height / 2;
  return { x: cx - newW / 2, y: cy - newH / 2, width: newW, height: newH };
}

export async function removeBgFromUrl(api, url) {
  const { data } = await api.post("/utils/remove-bg", { url });
  return data.url;
}

export function replaceWord(text, word, suggestion) {
  if (!word || text == null) return text;
  const esc = String(word).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  try {
    const re = new RegExp(`(^|[^\\p{L}\\p{N}])(${esc})(?=[^\\p{L}\\p{N}]|$)`, "gu");
    return String(text).replace(re, (m, p1) => p1 + suggestion);
  } catch (e) {
    return String(text).split(word).join(suggestion);
  }
}

export const ROTATE_180 = [-1, 0, 0, -1];

export function isRotated180(transform) {
  return Array.isArray(transform) && transform.length === 4 && transform.every((v, i) => Math.abs(v - ROTATE_180[i]) < 1e-6);
}

export function detectInheritedTransform(overlays, box) {
  if (!box) return null;
  const cx = box.x + box.width / 2, cy = box.y + box.height / 2;
  let best = null, bestDist = Infinity;
  for (const ov of overlays || []) {
    if (!ov.transform) continue;
    const ocx = (ov.x ?? 0) + (ov.width ?? 0) / 2, ocy = (ov.y ?? 0) + (ov.height ?? 0) / 2;
    const dist = Math.hypot(ocx - cx, ocy - cy);
    if (dist < bestDist) { bestDist = dist; best = ov.transform; }
  }
  return best;
}
